import React from "react";
import { Button } from "@/components/ui/Button/Button";
import { Typography } from "@/components/ui/Typography/Typography";
import { TRoleHierarchyPreview } from "@/api/role-management/role-management.types";
import { useTranslation } from "react-i18next";
import { Spinner } from "flowbite-react";

type TRoleHierarchyImpactModalProps = {
  open: boolean;
  preview?: TRoleHierarchyPreview | null;
  onCancel: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
};

export const RoleHierarchyImpactModal: React.FC<
  TRoleHierarchyImpactModalProps
> = ({ open, preview, onCancel, onConfirm, isLoading }) => {
  const { t } = useTranslation(["roleManagementPage"]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="relative w-full max-w-lg rounded-lg bg-white dark:bg-background border border-gray-200 dark:border-gray-700 shadow-xl p-6 space-y-4">
        {isLoading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/70"
            aria-busy="true"
            aria-live="polite"
          >
            <Spinner size="lg" />
          </div>
        )}
        <Typography variant="h6" affects="medium">
          {t("ROLES.HIERARCHY.IMPACT_MODAL_TITLE")}
        </Typography>
        <Typography variant="small" className="text-muted-foreground">
          {t("ROLES.HIERARCHY.IMPACT_MODAL_BODY", {
            count: preview?.invalid_relationship_count ?? 0,
            employees: preview?.affected_employee_count ?? 0,
          })}
        </Typography>

        {preview?.invalid_relationships?.length ? (
          <ul className="max-h-40 overflow-y-auto text-sm space-y-1 border rounded-md p-3">
            {preview.invalid_relationships.slice(0, 10).map((item) => (
              <li key={item.relationship_id}>
                {item.parent_name} → {item.child_name}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t("GLOBAL.CANCEL")}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {t("ROLES.HIERARCHY.CONFIRM_CHANGE")}
            {isLoading && <Spinner size="sm" className="ms-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
